import { BeakerIcon } from "@heroicons/react/24/outline";
import { CalendarDaysIcon, UserIcon } from "@heroicons/react/24/solid";
import FormInputComponent from "@via/components/inputs/form-input-component";
import Modal from "@via/components/modal.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import ProjectsFilter from "@via/pages/projects/projects-filter.tsx";
import {
  useCreateProject,
  useProjectList,
} from "@via/pages/projects/projects.api.tsx";
import { useCreateRunAttempt } from "@via/pages/projects/runs/runs.api.tsx";
import RunPermissionsSection from "@via/pages/projects/runs/run-permissions-section";
import { ModalSize } from "@via/utils/constants";
import { createRunOptions } from "@via/utils/form-validators.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import {
  PermissionType,
  ProjectViewType,
  type ListProjectsResponseBodyItem,
  type NewRunAttemptRequestBody,
} from "common";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";

interface CreateRunModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string | undefined;
  pipelineId: string | undefined;
}

type FormRunAttemptRequestBody = Omit<NewRunAttemptRequestBody, "projectId"> & {
  projectId: number | undefined;
  inheritFromProject?: boolean;
};

const sort = [
  {
    id: "createdAt",
    desc: true,
  },
];

const CreateRunModal: FC<CreateRunModalProps> = ({
  open,
  setOpen,
  projectId,
  pipelineId,
}) => {
  const [searchedInput, setSearchedInput] = useState("");
  const [cumulativeProjects, setCumulativeProjects] = useState<
    ListProjectsResponseBodyItem[]
  >([]);
  const [missingProjects, setMissingProjects] = useState<
    ListProjectsResponseBodyItem[]
  >([]);
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >([{ id: "type", value: ProjectViewType.MyProjects }]);

  const [projectsPage, setProjectsPage] = useState(0);
  const projectOptionSize = 10;

  const { data: projects, isPending: isProjectsLoading } = useProjectList(
    projectsPage,
    projectOptionSize,
    sort,
    [...columnFilters, { id: "name", value: searchedInput }]
  );

  // Fetch missing projects separately
  const { data: missingProjectListData, isLoading: isMissingProjectLoading } =
    useProjectList(
      0,
      1,
      [], // no sort
      [{ id: "id", value: projectId ?? "" }],
      projectId ? true : false // only fetch if there is a missing project
    );

  const { mutateAsync: createRun, isPending: isCreateRunLoading } =
    useCreateRunAttempt();

  const { resetChangeCount } = useChangesDetect();

  useEffect(() => {
    if (missingProjectListData?.data) {
      setMissingProjects(missingProjectListData.data);
    }
  }, [missingProjectListData]);

  const fetchMoreProjects = useCallback(() => {
    setProjectsPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((projects?.total ?? 0) / projectOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [projects?.total, projectOptionSize]);

  useEffect(() => {
    setCumulativeProjects([]);
  }, [columnFilters]);

  useEffect(() => {
    if (!projects) return;

    setCumulativeProjects(prevProjects => {
      const newData = projects.data ?? [];

      if (!newData.length) return [];

      // Append new results while preventing duplicates
      const existingIds = new Set(prevProjects.map(p => p.id));
      const newProjects = newData.filter(p => !existingIds.has(p.id));

      return [...prevProjects, ...newProjects];
    });
  }, [projects?.data, projectsPage, searchedInput, columnFilters]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setProjectsPage(0);
    setSearchedInput(value);
  }, 300);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormRunAttemptRequestBody>({
    mode: "onChange",
    defaultValues: {
      name: "",
      projectId: projectId ? parseInt(projectId ?? "", 10) : undefined,
      pipelineId: pipelineId ? parseInt(pipelineId ?? "", 10) : undefined,
      inheritFromProject: true,
    },
  });

  const {
    mutate: createProject,
    data: createProjectData,
    isPending: isCreateProjectLoading,
    isSuccess: isCreateProjectSuccess,
  } = useCreateProject();

  useEffect(() => {
    if (createProjectData) {
      const { id } = createProjectData;
      setValue("projectId", id, { shouldValidate: true });
    }
  }, [isCreateProjectSuccess, createProjectData]);

  // Resolves true once the run is created, false on an invalid form (the dialog stays open);
  // a failed request rejects, which also keeps it open. resetChangeCount stays ahead of the
  // await on purpose: the hook navigates to the new run on success, and the pipeline page's
  // unsaved changes blocker would stop that navigation if the count were still set.
  const handleConfirmModal = async (): Promise<boolean> => {
    let created = false;
    await handleSubmit(async (payload: FormRunAttemptRequestBody) => {
      const request = createRun(payload);
      resetChangeCount();
      await request;
      created = true;
    })();
    return created;
  };

  const handleCreateNewProject = (name: string): void => {
    createProject({
      name,
      permission: PermissionType.UserOwned,
      summary: "",
    });
  };

  const projectListOptions = useMemo(() => {
    const map = new Map<number, ListProjectsResponseBodyItem>();
    cumulativeProjects.forEach(project => map.set(project.id, project));
    missingProjects.forEach(project => map.set(project.id, project));
    const mergedProjects = Array.from(map.values());

    return (
      mergedProjects?.map(({ name, id, owner, updatedAt }) => ({
        label: name,
        value: id,
        metadata: { name, owner, updatedAt },
      })) ?? []
    );
  }, [cumulativeProjects, missingProjects]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Create a New Run"
      confirmText="Create"
      size={ModalSize.Large}
      isConfirmLoading={isCreateRunLoading}
      onConfirm={handleConfirmModal}>
      <form onSubmit={e => e.preventDefault()}>
        <div className="my-5">
          <div className="mt-2">
            <ProjectsFilter
              hideInputFilters
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setProjectsPage}
              setPageSize={() => {}}
            />
            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
              Choose Project for Run<span className="text-red-400">*</span>
            </label>
            <div className="mb-4">
              <SelectComponent
                control={control}
                name="projectId"
                rules={createRunOptions.projectId}
                options={projectListOptions}
                isLoading={
                  isProjectsLoading ||
                  isCreateProjectLoading ||
                  isMissingProjectLoading
                }
                placeholder={"Choose a Project or Type to Create New"}
                prefixIcon={<BeakerIcon />}
                isCreatable={true}
                onCreateOption={handleCreateNewProject}
                onFetchMoreOptions={fetchMoreProjects}
                onInputChange={inputValue =>
                  debouncedHandleInputChange(inputValue)
                }
                formatOptionLabel={(option, { context }) =>
                  context === "menu" ? (
                    <div className="flex flex-col gap-1">
                      {/* Project Name */}
                      <span className="font-semibold text-black">
                        {option.metadata?.name || option.label}
                      </span>

                      {option.metadata && (
                        <div className="text-sm text-gray-600">
                          {/* Owner */}
                          <div className="flex items-center gap-1">
                            <UserIcon className="h-4 w-4 text-indigo-600" />
                            <span className="font-medium">Owner:</span>
                            <span>{option.metadata.owner}</span>
                          </div>

                          {/* Last Updated Date */}
                          <div className="flex items-center gap-1">
                            <CalendarDaysIcon className="h-4 w-4 text-red-600" />
                            <span className="font-medium">Last updated:</span>
                            <span>
                              {formatDateString(
                                option.metadata.updatedAt,
                                "LLL"
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>{option.label}</span>
                  )
                }
              />
              {errors.projectId && (
                <span className="text-xs text-rose-500">
                  {errors.projectId.message}
                </span>
              )}
            </div>
            <FormInputComponent
              name="name"
              label="Run Name"
              showRequiredField
              control={control}
              validations={createRunOptions.name}
            />
          </div>

          {/* Permissions Section */}
          <RunPermissionsSection watch={watch} setValue={setValue} />
        </div>
      </form>
    </Modal>
  );
};

export default CreateRunModal;
